import React from 'react';
import { useNavigate } from 'react-router-dom';
import SendIcon from '@mui/icons-material/Send';
import { Button, CircularProgress, type SxProps, type Theme, Tooltip } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import { useClaimSubmit } from '../../store/hooks';

interface ClaimSubmitButtonProps {
  /** The claim ID to submit */
  claimId: string | undefined;
  /** Whether the form has blocking errors */
  hasErrors?: boolean;
  /** Optional custom button text */
  buttonText?: string;
  /** Optional button variant */
  variant?: 'text' | 'outlined' | 'contained';
  /** Optional button color */
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  /** Optional button size */
  size?: 'small' | 'medium' | 'large';
  /** Optional callback after submit completes */
  onSubmitComplete?: (success: boolean, blocked: boolean) => void;
  /** Optional custom styles */
  sx?: SxProps<Theme>;
}

/**
 * Standalone Claim Submit Button Component
 * 
 * This component handles claim submission to the clearinghouse.
 * It's disabled when the form has errors but allows submission with warnings/info.
 */
export const ClaimSubmitButton: React.FC<ClaimSubmitButtonProps> = ({
  claimId,
  hasErrors = false,
  buttonText = 'Submit Claim',
  variant = 'contained',
  color = 'primary',
  size = 'medium',
  onSubmitComplete,
  sx,
}) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { submitClaim, isSubmitting } = useClaimSubmit();
  const { billing: billingMetrics } = useEventTracking();

  const handleClick = async () => {
    if (!claimId) {
      enqueueSnackbar('Cannot submit claim without ID', { variant: 'error' });
      return;
    }

    if (hasErrors) {
      enqueueSnackbar('Please fix all errors before submitting', { variant: 'error' });
      return;
    }

    const result = await submitClaim(claimId);
    
    if (result.success) {
      if (result.submissionBlocked) {
        // Submission was blocked due to validation errors
        billingMetrics.submitClaim(claimId, {
          submission_method: 'manual',
          claim_status: 'blocked',
        });
        // The panel will automatically open (handled in useClaimSubmit)
        enqueueSnackbar('Submission blocked. Please review and fix validation errors.', { 
          variant: 'warning' 
        });
        onSubmitComplete?.(false, true);
      } else {
        // Successful submission
        billingMetrics.submitClaim(claimId, {
          submission_method: 'manual',
          claim_status: 'submitted',
        });
        
        enqueueSnackbar('Claim submitted successfully', { variant: 'success' });
        onSubmitComplete?.(true, false);
        // Navigate back to claims list
        navigate('/billing?tab=claims');
      }
    } else {
      billingMetrics.submitClaim(claimId, {
        submission_method: 'manual',
        claim_status: 'failed',
      });
      enqueueSnackbar('Failed to submit claim. Please try again.', { variant: 'error' });
      onSubmitComplete?.(false, false);
    }
  };

  // Determine tooltip message
  let tooltipMessage = 'Submit claim to clearinghouse';
  if (!claimId) {
    tooltipMessage = 'Save claim before submitting';
  } else if (hasErrors) {
    tooltipMessage = 'Fix all errors before submitting';
  } else if (isSubmitting) {
    tooltipMessage = 'Submitting claim...';
  }

  return (
    <Tooltip 
      title={tooltipMessage}
      placement="top"
    >
      <span>
        <Button
          variant={variant}
          color={color}
          size={size}
          onClick={handleClick}
          disabled={!claimId || hasErrors || isSubmitting}
          startIcon={
            isSubmitting ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <SendIcon />
            )
          }
          sx={{
            minWidth: 140,
            ...(sx as object),
          }}
        >
          {isSubmitting ? 'Submitting...' : buttonText}
        </Button>
      </span>
    </Tooltip>
  );
};